import { create } from 'zustand';
import { User } from '../types';

// Token will be valid for 30 days
const TOKEN_EXPIRY_DAYS = 30;

const getStoredCredential = () => {
    const credential = localStorage.getItem('credential');
    const expiryTime = localStorage.getItem('credentialExpiry');
    
    if (!credential || !expiryTime) return null;
    
    // Check if token has expired
    if (new Date().getTime() > parseInt(expiryTime)) {
        localStorage.removeItem('credential');
        localStorage.removeItem('credentialExpiry');
        localStorage.removeItem('user');
        return null;
    }
    
    return credential;
};

interface AuthState {
    user: User | null;
    credential: string | null;
    isAuthenticated: boolean;
    isLoading: boolean;
    error: string | null;
    setUser: (user: User | null, credential: string | null) => void;
    login: (googleUser: any, credential: string) => Promise<void>;
    logout: () => void;
    clearError: () => void;
}

export const useAuthStore = create<AuthState>((set, get) => ({
    user: JSON.parse(localStorage.getItem('user') || 'null'),
    credential: getStoredCredential(),
    isAuthenticated: !!getStoredCredential() && !!localStorage.getItem('user'),
    isLoading: false,
    error: null,
    setUser: (user, credential) => {
        console.log('Setting user:', user);
        if (user) {
            localStorage.setItem('user', JSON.stringify(user));
        }
        if (credential) {
            localStorage.setItem('credential', credential);
            // Set expiry time
            const expiryTime = new Date().getTime() + (TOKEN_EXPIRY_DAYS * 24 * 60 * 60 * 1000);
            localStorage.setItem('credentialExpiry', expiryTime.toString());
        }
        set({ user, credential, isAuthenticated: !!user });
    },
    login: async (googleUser, credential) => {
        console.log('Starting login process with user:', googleUser);
        
        try {
            set({ isLoading: true, error: null });
            
            if (!googleUser || !googleUser.email || !credential) {
                throw new Error('Invalid user data received');
            }

            const user: User = {
                email: googleUser.email,
                name: googleUser.name || googleUser.email.split('@')[0],
                picture: googleUser.picture || null,
            };
            
            // Simulate a small delay to ensure state updates are visible
            await new Promise(resolve => setTimeout(resolve, 100));
            
            console.log('Setting authenticated user:', user);
            localStorage.setItem('user', JSON.stringify(user));
            localStorage.setItem('credential', credential);
            // Set expiry time
            const expiryTime = new Date().getTime() + (TOKEN_EXPIRY_DAYS * 24 * 60 * 60 * 1000);
            localStorage.setItem('credentialExpiry', expiryTime.toString());
            
            set({ 
                user, 
                credential,
                isAuthenticated: true, 
                isLoading: false, 
                error: null 
            });
        } catch (error) {
            console.error('Login error in store:', error);
            set({ 
                error: error instanceof Error ? error.message : 'Failed to login',
                isLoading: false,
                user: null,
                credential: null,
                isAuthenticated: false
            });
            throw error;
        }
    },
    logout: () => {
        localStorage.removeItem('user');
        localStorage.removeItem('credential');
        localStorage.removeItem('credentialExpiry');
        set({ 
            user: null, 
            credential: null,
            isAuthenticated: false, 
            error: null,
            isLoading: false 
        });
    },
    clearError: () => {
        console.log('Clearing error');
        set({ error: null });
    }
}));
